import {
  AlertCircle,
  Ban,
  Briefcase,
  CheckCircle2,
  Clock,
  ExternalLink,
  Inbox,
  Loader2,
  MessageSquare,
  Play,
  Wrench,
  XCircle,
} from 'lucide-react';
import { AppLink } from '@/components/ui/app-link';
import { ProvenanceFileList } from './ProvenanceFileList';
import { ProvenancePhotoList } from './ProvenancePhotoList';
import { ProvenanceRecordingList } from './ProvenanceRecordingList';
import { ProvenanceVideoList } from './ProvenanceVideoList';
import { ProvenanceContentCard } from './ProvenanceContentCard';
import { ProvenanceWork4MeEntityCard } from './ProvenanceWork4MeEntityCard';
import { ProvenanceContentReferenceListCard } from './ProvenanceContentReferenceListCard';
import { ProvenanceContentReadCard } from './ProvenanceContentReadCard';
import { ProvenanceMessageReviewCard } from './ProvenanceMessageReviewCard';
import { ProvenanceDriveEntryListCard } from './ProvenanceDriveEntryListCard';
import { ProvenanceDriveFileCard } from './ProvenanceDriveFileCard';
import { ProvenanceMailboxEnvelopeListCard } from './ProvenanceMailboxEnvelopeListCard';
import { ProvenanceMailboxMessageCard } from './ProvenanceMailboxMessageCard';
import { ProvenancePageNavigationChip } from './ProvenancePageNavigationChip';
import { ProvenanceExternalConnectCard } from './ProvenanceExternalConnectCard';
import { ProvenanceViewNavigationChip } from './ProvenanceViewNavigationChip';
import { ProvenanceDraftSendReadyCard } from './ProvenanceDraftSendReadyCard';
import { ProvenanceComposeDraftReadyChip } from './ProvenanceComposeDraftReadyChip';
import { ProvenanceAnchorProposalCard } from './ProvenanceAnchorProposalCard';
import { ProvenanceObservationCard } from './ProvenanceObservationCard';
import { canSplitToNewObservation, observationOptions, type ObservationProjection } from './observation-labels';
import type { MoveObservationMediaResult } from '@vivport/backend/types';
import { ProvenanceRawDetail } from './ProvenanceRawDetail';
import { ProvenanceAssistantMessage, type Density } from './ProvenanceAssistantMessage';
import { ProvenanceMemberBubble } from './ProvenanceMemberBubble';
import { ProvenanceCollapsedSequence } from './ProvenanceCollapsedSequence';
import { ProvenanceReasoningCard } from './ProvenanceReasoningCard';
import type { AgentHistoryItem } from './provenance-entry-item';
import { COMPOSER_LLM_MODELS } from '../../features/speak2me/components/ChatComposerModels';

const MODEL_DISPLAY_NAMES: Record<string, string> = Object.fromEntries(
  COMPOSER_LLM_MODELS.map((m) => [m.id, m.label]),
);

export const THINKING_ITEM_ID = 'assistant-thinking-placeholder';

export type ProvenanceContext = {
  observations?: ObservationProjection;
  onRequestCapture?: (args: { mode: 'recording' | 'photo' | 'video'; linkContentId?: number }) => void;
  audioUrlsByRecordingItemId?: Record<string, string>;
  onOpenContent?: (contentId: number) => void;
  onObservationMediaMoved?: (result: MoveObservationMediaResult) => void;
  density?: Density;
};

export type ProvenanceItemRenderer<K extends AgentHistoryItem['kind']> = (
  item: Extract<AgentHistoryItem, { kind: K }>,
  context?: ProvenanceContext,
) => JSX.Element;

export type ProvenanceItemRegistry = {
  [K in AgentHistoryItem['kind']]: ProvenanceItemRenderer<K>;
};

const CONTENT_ID_FIELDS = ['id', 'contentId', 'documentId', 'anchorId', 'eventId'] as const;

function extractContentIdFromToolResult(
  item: Extract<AgentHistoryItem, { kind: 'tool_result' }>
): number | null {
  if (item.status !== 'ok') return null;
  if (!item.output || typeof item.output !== 'object') return null;
  const out = item.output as Record<string, unknown>;
  for (const field of CONTENT_ID_FIELDS) {
    const candidate = out[field];
    if (typeof candidate === 'number' && Number.isInteger(candidate) && candidate > 0) {
      return candidate;
    }
  }
  return null;
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function humanizeKindLabel(kind: string): string {
  const spaced = kind.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

type MinimalWorkerKind =
  | 'trigger_received'
  | 'enqueued'
  | 'execution_started'
  | 'execution_completed'
  | 'execution_failed';

type MinimalWorkerItem = Extract<AgentHistoryItem, { kind: MinimalWorkerKind }>;

function renderMinimalWorkerCard(item: MinimalWorkerItem): JSX.Element {
  return (
    <div className="w-full text-sm text-foreground rounded border p-3 space-y-1 bg-muted/30">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium">{humanizeKindLabel(item.kind)}</p>
        <p className="text-xs text-muted-foreground">{formatHistoryTimestamp(item.occurredAt)}</p>
      </div>
      <p className="text-xs text-muted-foreground">Role: {item.role}</p>
      {item.body ? <p className="text-sm whitespace-pre-wrap">{item.body}</p> : null}
      {item.payload !== null ? <ProvenanceRawDetail value={item.payload} /> : null}
    </div>
  );
}

// One Observation renders exactly one card, at the row that first brought the group into
// the history — a capture row for a captured Observation, a move row for one split out.
// Both doors call this so the card cannot drift between them.
function renderObservationCard(
  itemId: string,
  groupId: number,
  context: ProvenanceContext | undefined,
): JSX.Element {
  const projection = context?.observations;
  const merged = projection?.mergedByGroupId.get(groupId);
  if (!merged || !projection?.cardItemIds.has(itemId)) return <></>;
  return (
    <ProvenanceObservationCard
      observation={merged}
      label={projection.labelByGroupId.get(groupId) ?? ''}
      audioUrlsById={context?.audioUrlsByRecordingItemId}
      onOpenContent={context?.onOpenContent}
      moveTargets={observationOptions(projection).filter((option) => option.groupId !== groupId)}
      allowNewObservation={canSplitToNewObservation(projection)}
      onMoved={context?.onObservationMediaMoved}
    />
  );
}

export const provenanceItemRegistry: ProvenanceItemRegistry = {
  assistant_message: (item, context) => {
    const density: Density = context?.density ?? 'compact';
    if (item.id === THINKING_ITEM_ID) {
      return (
        <div className="w-full text-sm text-muted-foreground">
          <div className="inline-flex items-center gap-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2">
            <Loader2 className="h-4 w-4 animate-spin text-neo" />
            <span className="text-neo">Neo is thinking…</span>
          </div>
        </div>
      );
    }
    return (
      <div className="w-full text-sm text-foreground">
        <ProvenanceAssistantMessage text={item.text} density={density} />
      </div>
    );
  },
  member_message: (item, context) => {
    const density: Density = context?.density ?? 'compact';
    return <ProvenanceMemberBubble text={item.text} density={density} />;
  },
  document_recordings: (item, context) => (
    <div className="w-full text-sm text-foreground">
      <ProvenanceRecordingList
        draftText=""
        recordings={item.recordingIds.map((recordingId) => ({
          id: `recording-${recordingId}`,
          remoteId: recordingId,
          name: `Recording ${recordingId}`,
        }))}
        onAddUnlinked={
          context?.onRequestCapture ? () => context.onRequestCapture!({ mode: 'recording' }) : undefined
        }
        onAddLinked={
          context?.onRequestCapture
            ? (linkContentId?: number) => context.onRequestCapture!({ mode: 'recording', linkContentId })
            : undefined
        }
        audioUrlsById={context?.audioUrlsByRecordingItemId}
        onOpenContent={context?.onOpenContent}
      />
    </div>
  ),
  document_videos: (item, context) => (
    <div className="w-full text-sm text-foreground">
      <ProvenanceVideoList
        videos={item.videoIds.map((videoId) => ({
          id: `video-${videoId}`,
          remoteId: videoId,
          name: `Video ${videoId}`,
        }))}
        onAddUnlinked={
          context?.onRequestCapture ? () => context.onRequestCapture!({ mode: 'video' }) : undefined
        }
        onAddLinked={
          context?.onRequestCapture
            ? (linkContentId?: number) => context.onRequestCapture!({ mode: 'video', linkContentId })
            : undefined
        }
        onOpenContent={context?.onOpenContent}
      />
    </div>
  ),
  document_photos: (item, context) => (
    <div className="w-full text-sm text-foreground">
      <ProvenancePhotoList
        photos={item.photoIds.map((photoId) => ({
          id: `photo-${photoId}`,
          remoteId: photoId,
          name: `Photo ${photoId}`,
          url: '',
        }))}
        onAddUnlinked={
          context?.onRequestCapture ? () => context.onRequestCapture!({ mode: 'photo' }) : undefined
        }
        onAddLinked={
          context?.onRequestCapture
            ? (linkContentId?: number) => context.onRequestCapture!({ mode: 'photo', linkContentId })
            : undefined
        }
        onOpenContent={context?.onOpenContent}
      />
    </div>
  ),
  document_files: (item, context) => (
    <div className="w-full text-sm text-foreground">
      <ProvenanceFileList fileIds={item.fileIds} onOpenContent={context?.onOpenContent} />
    </div>
  ),
  uploaded_files: (item, context) => (
    <div className="w-full text-sm text-foreground">
      <ProvenanceFileList
        fileIds={item.fileIds}
        heading="Uploaded files"
        onOpenContent={context?.onOpenContent}
      />
    </div>
  ),
  event_import_result: (item, context) => (
    <div className="w-full text-sm text-foreground rounded border p-2 space-y-1">
      <p className="font-medium">Event import summary</p>
      <p className="text-xs text-muted-foreground">
        Status: {item.status} | Files processed: {item.filesProcessed} | Schema v{item.schemaVersion}
      </p>
      <p className="text-xs text-muted-foreground">
        Parsed: {item.totals.parsedRows} | Valid: {item.totals.validRows}
      </p>
      <p className="text-xs text-muted-foreground">
        Created: {item.totals.created} | Conflicts: {item.totals.conflicts} | Errors: {item.totals.errors}
      </p>
      {item.createdEventIds.length > 0 ? (
        <p className="text-xs text-muted-foreground">Created event IDs: {item.createdEventIds.join(', ')}</p>
      ) : null}
      {item.createdAnchorIds.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          Created anchor IDs:{' '}
          {item.createdAnchorIds.map((anchorId, index) => (
            <span key={anchorId}>
              {index > 0 ? ', ' : null}
              <button
                type="button"
                className="underline-offset-2 hover:underline font-medium"
                onClick={() => context?.onOpenContent?.(anchorId)}
              >
                {anchorId}
              </button>
            </span>
          ))}
        </p>
      ) : null}
      {item.sourceFileIds.length > 0 ? (
        <ProvenanceFileList
          fileIds={item.sourceFileIds}
          heading="Imported source files"
          onOpenContent={context?.onOpenContent}
        />
      ) : null}
      {item.extractedJsonFileIds.length > 0 ? (
        <ProvenanceFileList
          fileIds={item.extractedJsonFileIds}
          heading="Normalized JSON output"
          onOpenContent={context?.onOpenContent}
        />
      ) : null}
      {item.failure ? <p className="text-xs text-destructive">Import failure: {item.failure.message}</p> : null}
    </div>
  ),
  event_automation_created: (item) => (
    <div className="w-full text-sm text-foreground rounded border p-3 space-y-1 bg-muted/30">
      <p className="font-medium">Event import automation enabled</p>
      <p className="text-xs text-muted-foreground">Process: {item.name}</p>
      <p className="text-xs text-muted-foreground">Trigger: {item.triggerType} ({item.uploadPurpose})</p>
      <p className="text-xs text-muted-foreground">File types: {item.allowedExtensions.join(', ')}</p>
      <p className="text-xs text-muted-foreground">Process ID: {item.processId}</p>
    </div>
  ),
  event_document_section_created: (item, context) => (
    <ProvenanceContentCard
      contentId={item.documentSectionId}
      fallbackName={item.title?.trim() ?? ''}
      fallbackDescription={item.description}
      onOpenContent={context?.onOpenContent}
    />
  ),
  event_content_created: (item, context) => (
    <ProvenanceContentCard
      contentId={item.contentId}
      fallbackName={item.title?.trim() ?? ''}
      fallbackDescription={item.description}
      onOpenContent={context?.onOpenContent}
    />
  ),
  event_work4me_entity_created: (item) => <ProvenanceWork4MeEntityCard item={item} />,
  content_reference_list: (item) => <ProvenanceContentReferenceListCard item={item} />,
  content_read_card: (item) => <ProvenanceContentReadCard item={item} />,
  message_review_card: (item) => <ProvenanceMessageReviewCard item={item} />,
  mailbox_envelope_list: (item) => <ProvenanceMailboxEnvelopeListCard item={item} />,
  mailbox_message_card: (item) => <ProvenanceMailboxMessageCard item={item} />,
  drive_entry_list: (item) => <ProvenanceDriveEntryListCard item={item} />,
  drive_file_card: (item) => <ProvenanceDriveFileCard item={item} />,
  view_navigation: (item) => <ProvenanceViewNavigationChip item={item} />,
  page_navigation: (item) => <ProvenancePageNavigationChip item={item} />,
  external_connect_offer: (item) => <ProvenanceExternalConnectCard item={item} />,
  draft_send_ready: (item) => <ProvenanceDraftSendReadyCard item={item} />,
  compose_draft_ready: (item) => <ProvenanceComposeDraftReadyChip item={item} />,
  event_compose_ready: (item) => (
    <div className="rounded-lg border border-border p-3 text-sm">
      <p className="font-medium">Event ready to review</p>
      <p className="mt-1 truncate text-xs text-muted-foreground">{item.title}</p>
    </div>
  ),
  document_section_proposal_ready: (item) => (
    <div className="rounded-lg border border-border p-3 text-sm">
      <p className="font-medium">Section text ready to review</p>
      <p className="mt-1 truncate text-xs text-muted-foreground">{item.sectionTitle || item.sectionCode}</p>
    </div>
  ),
  anchor_proposal_ready: (item) => <ProvenanceAnchorProposalCard item={item} />,
  // The live tool lives in the composer slot and the Observation card is the record of
  // what it produced, so the request itself has nothing left to render.
  capture_request: () => <></>,
  // The live control sits in the actions row and the member's "Label: value" line is the record.
  field_input_offered: () => <></>,
  // The live form sits in the actions row and the member's "Added/Updated …" line is the record.
  entry_form_offered: () => <></>,
  observation_media: (item, context) =>
    renderObservationCard(item.id, item.observationGroupId, context),
  // A move is folded into the card by projectObservations and is silent as a feed row —
  // the correction shows as the item having moved, not as a line of chatter. The one
  // exception is a split: the destination Observation is born at this row, so this is
  // where its card belongs in the history.
  observation_media_moved: (item, context) =>
    renderObservationCard(item.id, item.toObservationGroupId, context),
  event_process_created: (item) => (
    <AppLink
      to={`/work4me/processes/${encodeURIComponent(item.processId)}`}
      className="block w-full rounded-lg border border-border p-3 text-sm text-foreground hover:bg-muted/40 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 shrink-0" />
            <p className="font-medium truncate">{item.name}</p>
          </div>
          <p className="text-xs mt-1 text-muted-foreground truncate">
            Trigger: {item.triggerType} (tagId {item.tagId})
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <span className="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium">
            PROCESS
          </span>
          <span className="text-xs text-primary inline-flex items-center gap-1">
            Open in Work4Me
            <ExternalLink className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </AppLink>
  ),
  event_process_updated: (item) => (
    <div className="w-full text-sm text-foreground rounded border p-3 space-y-1 bg-muted/30">
      <p className="font-medium">Process updated</p>
      <p className="text-xs text-muted-foreground">Name: {item.name}</p>
      <p className="text-xs text-muted-foreground">
        Trigger: {item.triggerType} (tagId: {item.tagId}
        {item.contentType ? `, contentType: ${item.contentType}` : ''})
      </p>
      <p className="text-xs text-muted-foreground">Agent: {item.agentId}</p>
      <p className="text-xs text-muted-foreground">
        Skills: {item.skillIds.length > 0 ? item.skillIds.join(', ') : 'none'}
      </p>
      <p className="text-xs text-muted-foreground">Status: {item.enabled ? 'ENABLED' : 'DISABLED'}</p>
      <p className="text-xs text-muted-foreground">Process ID: {item.processId}</p>
      <AppLink
        to={`/work4me/processes/${encodeURIComponent(item.processId)}`}
        className="inline-block text-xs font-medium text-primary underline pt-1"
      >
        Open in Work4Me Processes
      </AppLink>
    </div>
  ),
  event_attachment_completed: (item) => (
    <div className="w-full text-sm text-foreground">
      <p className="font-medium">Attachment result</p>
      <p>{item.message}</p>
      <p className="text-xs mt-1">
        Content {item.documentSectionId}
        {item.eventId ? ` -> Event ${item.eventId}` : ''}
        {item.anchorId ? ` (Anchor ${item.anchorId})` : ''}
      </p>
    </div>
  ),
  event_visit_document_section_linked: (item) => (
    <div className="w-full text-sm text-foreground">
      <p className="font-medium">Visit content linked</p>
      <p>
        Content {item.documentSectionId} linked to event {item.eventId}
      </p>
    </div>
  ),
  event_visit_ended: (item) => (
    <div className="w-full text-sm text-foreground">
      <p className="font-medium">Visit ended</p>
      <p>{item.summary}</p>
      <p className="text-xs mt-1">Items captured: {item.documentSectionCount}</p>
    </div>
  ),
  pending_approval: (item) => (
    <div className="w-full text-sm text-foreground">
      <p className="font-medium">Pending approval</p>
      <p>{item.message}</p>
      <p className="text-xs mt-1">Run ID: {item.runId}</p>
    </div>
  ),
  tool_call: (item) => (
    <div className="w-full text-xs text-muted-foreground inline-flex items-center gap-2">
      <Wrench className="h-3.5 w-3.5 shrink-0" />
      <span className="font-mono">{item.toolKey}</span>
    </div>
  ),
  collapsed_sequence: (item, context) => (
    <ProvenanceCollapsedSequence items={item.items} context={context} />
  ),
  reasoning_section: (item) => <ProvenanceReasoningCard item={item} />,
  model_marker: (item) => {
    const displayName = MODEL_DISPLAY_NAMES[item.model] ?? item.model;
    const previousDisplayName = item.previousModel
      ? MODEL_DISPLAY_NAMES[item.previousModel] ?? item.previousModel
      : null;
    const text = item.reason === 'initial'
      ? `Using ${displayName}`
      : `Model changed to ${displayName}${previousDisplayName ? ` (from ${previousDisplayName})` : ''}`;
    return (
      <div className="my-2 flex items-center gap-2 text-xs text-muted-foreground/70">
        <div className="h-px flex-1 bg-border" />
        <span className="px-2">{text}</span>
        <div className="h-px flex-1 bg-border" />
      </div>
    );
  },
  tool_result: (item, context) => {
    if (item.status === 'ok') {
      const contentId = extractContentIdFromToolResult(item);
      if (contentId !== null) {
        return (
          <ProvenanceContentCard
            contentId={contentId}
            fallbackName={item.summary ?? item.toolKey}
            onOpenContent={context?.onOpenContent}
          />
        );
      }
      return (
        <div className="w-full text-xs inline-flex items-start gap-2">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5 text-success" />
          <span className="flex-1 min-w-0">
            <span className="block text-foreground">{item.summary ?? item.toolKey}</span>
            <span className="block text-muted-foreground font-mono text-[0.7rem]">{item.toolKey}</span>
          </span>
        </div>
      );
    }
    if (item.status === 'error') {
      const errorSummary = item.errorMessage ? truncate(item.errorMessage, 120) : 'Tool execution failed';
      return (
        <div className="w-full text-xs inline-flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-destructive" />
          <span className="flex-1 min-w-0">
            <span className="block text-destructive">{errorSummary}</span>
            <span className="block text-muted-foreground font-mono text-[0.7rem]">{item.toolKey}</span>
          </span>
        </div>
      );
    }
    return (
      <div className="w-full text-xs inline-flex items-start gap-2">
        <Clock className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground" />
        <span className="flex-1 min-w-0">
          <span className="block text-foreground">{item.summary ?? 'Awaiting approval'}</span>
          <span className="block text-muted-foreground font-mono text-[0.7rem]">{item.toolKey}</span>
        </span>
      </div>
    );
  },
  event_trigger_received: (item) => (
    <div className="w-full text-sm text-foreground rounded border p-3 space-y-1 bg-muted/30">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Inbox className="h-4 w-4 shrink-0" />
          <p className="font-medium">Trigger received{item.triggerType ? ` (${item.triggerType})` : ''}</p>
        </div>
        <p className="text-xs text-muted-foreground">{formatHistoryTimestamp(item.occurredAt)}</p>
      </div>
      <ProvenanceRawDetail value={item.rawDetail} />
    </div>
  ),
  event_execution_started: (item) => (
    <div className="w-full text-sm text-foreground rounded border p-3 space-y-1 bg-muted/30">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Play className="h-4 w-4 shrink-0" />
          <p className="font-medium">{item.summary}</p>
        </div>
        <p className="text-xs text-muted-foreground">{formatHistoryTimestamp(item.occurredAt)}</p>
      </div>
      <p className="text-xs text-muted-foreground">Phase: {item.phase} | Status: {item.status}</p>
      <ProvenanceRawDetail value={item.rawDetail} />
    </div>
  ),
  event_tool_call: (item) => {
    const outputPreview = summarizeToolOutput(item.output);
    return (
      <div className="w-full text-sm text-foreground rounded border p-3 space-y-1 bg-muted/30">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 shrink-0" />
            <p className="font-medium">Tool: {item.toolKey}</p>
          </div>
          <p className="text-xs text-muted-foreground">{formatHistoryTimestamp(item.occurredAt)}</p>
        </div>
        <p className="text-xs text-muted-foreground">
          Status: {item.toolStatus} | Kind: {item.entryKind} ({item.status})
        </p>
        {item.leaseAutonomy ? (
          <p className="text-xs text-muted-foreground">Ran under lease autonomy — no approval was asked.</p>
        ) : null}
        {outputPreview ? <p className="text-xs text-muted-foreground truncate">{outputPreview}</p> : null}
        {item.error ? <p className="text-xs text-destructive">{item.error}</p> : null}
        <ProvenanceRawDetail value={item.rawDetail} />
      </div>
    );
  },
  event_agent_reply: (item) => (
    <div className="w-full text-sm text-foreground rounded border p-3 space-y-1 bg-muted/30">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 shrink-0" />
          <p className="font-medium">Agent reply</p>
        </div>
        <p className="text-xs text-muted-foreground">{formatHistoryTimestamp(item.occurredAt)}</p>
      </div>
      <p className="whitespace-pre-wrap">{item.reply}</p>
      {item.agentRunId ? (
        <p className="text-xs text-muted-foreground">Agent run: {item.agentRunId}</p>
      ) : null}
    </div>
  ),
  event_execution_completed: (item) => (
    <div className="w-full text-sm text-foreground rounded border p-3 space-y-1 bg-muted/30">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <p className="font-medium">Execution completed</p>
        </div>
        <p className="text-xs text-muted-foreground">{formatHistoryTimestamp(item.occurredAt)}</p>
      </div>
      {item.agentRunStatus ? (
        <p className="text-xs text-muted-foreground">Agent run status: {item.agentRunStatus}</p>
      ) : null}
      {item.agentRunId ? (
        <p className="text-xs text-muted-foreground">Agent run: {item.agentRunId}</p>
      ) : null}
      <ProvenanceRawDetail value={item.rawDetail} />
    </div>
  ),
  event_execution_failed: (item) => (
    <div className="w-full text-sm text-foreground rounded border p-3 space-y-1 bg-muted/30">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 shrink-0" />
          <p className="font-medium">Execution failed</p>
        </div>
        <p className="text-xs text-muted-foreground">{formatHistoryTimestamp(item.occurredAt)}</p>
      </div>
      <p className="text-xs text-destructive">{item.errorMessage}</p>
      {item.reason ? <p className="text-xs text-muted-foreground">Reason: {item.reason}</p> : null}
      {item.agentRunId ? (
        <p className="text-xs text-muted-foreground">Agent run: {item.agentRunId}</p>
      ) : null}
      <ProvenanceRawDetail value={item.rawDetail} />
    </div>
  ),
  event_execution_cancelled: (item) => (
    <div className="w-full text-sm text-foreground rounded border p-3 space-y-1 bg-muted/30">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Ban className="h-4 w-4 shrink-0" />
          <p className="font-medium">Execution cancelled</p>
        </div>
        <p className="text-xs text-muted-foreground">{formatHistoryTimestamp(item.occurredAt)}</p>
      </div>
      {item.reason ? <p className="text-xs text-muted-foreground">Reason: {item.reason}</p> : null}
      {item.agentRunId ? (
        <p className="text-xs text-muted-foreground">Agent run: {item.agentRunId}</p>
      ) : null}
      <ProvenanceRawDetail value={item.rawDetail} />
    </div>
  ),
  event_anchor_decision: (item, context) => {
    const decisionStyle =
      item.decision === 'REUSE'
        ? 'bg-success-subtle text-success border-success-border'
        : item.decision === 'CREATE'
        ? 'bg-info-subtle text-info border-info-border'
        : 'bg-muted text-muted-foreground border-border';
    const visibleRationale = item.rationale.slice(0, 3);
    const extraRationale = item.rationale.slice(3);
    return (
      <div className="w-full text-sm text-foreground rounded border p-2 space-y-1">
        <div className="flex items-center justify-between gap-3">
          <p className="font-medium">Anchor decision</p>
          <p className="text-xs text-muted-foreground">{formatHistoryTimestamp(item.occurredAt)}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${decisionStyle}`}>
            {item.decision}
          </span>
          <span className="inline-flex items-center rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs text-muted-foreground">
            {item.sourceType}
          </span>
          <span className="text-xs text-muted-foreground truncate">{item.representativeLabel}</span>
        </div>
        {item.anchorId !== null ? (
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              className="underline-offset-2 hover:underline font-medium"
              onClick={() => context?.onOpenContent?.(item.anchorId as number)}
            >
              {item.anchorTitle ?? `Anchor ${item.anchorId}`}
            </button>
            <span className="inline-flex items-center rounded border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              {item.anchorCreated ? 'created' : 'existing'}
            </span>
          </div>
        ) : null}
        <p className="text-xs text-muted-foreground">
          Attached {item.attachedCount} | Skipped {item.skippedCount}
        </p>
        {visibleRationale.length > 0 ? (
          <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-0.5">
            {visibleRationale.map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        ) : null}
        {extraRationale.length > 0 ? (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer select-none hover:underline">
              +{extraRationale.length} more
            </summary>
            <ul className="list-disc pl-5 space-y-0.5 mt-1">
              {extraRationale.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
          </details>
        ) : null}
        <ProvenanceRawDetail value={item.rawDetail} />
      </div>
    );
  },
  event_pending_approval: (item) => (
    <div className="w-full text-sm text-foreground rounded border p-3 space-y-1 bg-muted/30">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 shrink-0" />
          <p className="font-medium">Pending approval</p>
        </div>
        <p className="text-xs text-muted-foreground">{formatHistoryTimestamp(item.occurredAt)}</p>
      </div>
      <p>{item.message}</p>
      {item.agentRunId ? (
        <p className="text-xs text-muted-foreground">Agent run: {item.agentRunId}</p>
      ) : null}
    </div>
  ),
  trigger_received: renderMinimalWorkerCard as ProvenanceItemRenderer<'trigger_received'>,
  enqueued: renderMinimalWorkerCard as ProvenanceItemRenderer<'enqueued'>,
  execution_started: renderMinimalWorkerCard as ProvenanceItemRenderer<'execution_started'>,
  execution_completed: renderMinimalWorkerCard as ProvenanceItemRenderer<'execution_completed'>,
  execution_failed: renderMinimalWorkerCard as ProvenanceItemRenderer<'execution_failed'>,
  manual_session_opened: (item) => (
    <div className="w-full text-xs text-muted-foreground flex items-center justify-between gap-2 px-3 py-1.5 border-l-2 border-border">
      <span>Session opened</span>
      <span>{formatHistoryTimestamp(item.occurredAt)}</span>
    </div>
  ),
  manual_session_closed: (item) => (
    <div className="w-full text-xs text-muted-foreground flex items-center justify-between gap-2 px-3 py-1.5 border-l-2 border-border">
      <span>Session closed{item.reason ? ` · ${item.reason}` : ''}</span>
      <span>{formatHistoryTimestamp(item.occurredAt)}</span>
    </div>
  ),
  manual_session_content_created: (item, context) => (
    <div className="w-full space-y-1">
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>Created</span>
        <span>{formatHistoryTimestamp(item.occurredAt)}</span>
      </div>
      <ProvenanceContentCard
        contentId={item.contentId}
        fallbackName=""
        onOpenContent={context?.onOpenContent}
      />
    </div>
  ),
  manual_session_content_updated: (item, context) => (
    <div className="w-full space-y-1">
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          Edited
          {item.changedFields.length > 0 ? ` · ${item.changedFields.join(', ')}` : ''}
        </span>
        <span>{formatHistoryTimestamp(item.occurredAt)}</span>
      </div>
      <ProvenanceContentCard
        contentId={item.contentId}
        fallbackName=""
        onOpenContent={context?.onOpenContent}
      />
    </div>
  ),
  manual_session_content_deleted: (item, context) => (
    <div className="w-full space-y-1">
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>Deleted</span>
        <span>{formatHistoryTimestamp(item.occurredAt)}</span>
      </div>
      <ProvenanceContentCard
        contentId={item.contentId}
        fallbackName=""
        onOpenContent={context?.onOpenContent}
      />
    </div>
  ),
};

function formatHistoryTimestamp(value: string | null): string {
  if (!value) {
    return 'N/A';
  }
  return new Date(value).toLocaleString();
}

function summarizeToolOutput(output: unknown): string | null {
  if (output === null || output === undefined) {
    return null;
  }
  if (typeof output === 'string') {
    return output.length > 120 ? `${output.slice(0, 120)}…` : output;
  }
  try {
    const serialized = JSON.stringify(output);
    if (!serialized) {
      return null;
    }
    return serialized.length > 120 ? `${serialized.slice(0, 120)}…` : serialized;
  } catch {
    return null;
  }
}

function getProvenanceItemRenderer<K extends AgentHistoryItem['kind']>(
  kind: K
): ProvenanceItemRenderer<K> | undefined {
  return provenanceItemRegistry[kind];
}

export function renderProvenanceItem(
  item: AgentHistoryItem,
  context?: ProvenanceContext
): JSX.Element {
  const renderItem = getProvenanceItemRenderer(item.kind) as
    | ((item: AgentHistoryItem, context?: ProvenanceContext) => JSX.Element)
    | undefined;
  // A running client can always be older than the server that produced the item, so an
  // unknown kind is a versioning fact, not an invariant break. Surface it in place —
  // never let one feed row take down the whole route.
  if (typeof renderItem !== 'function') {
    return (
      <div className="w-full text-xs italic text-muted-foreground/70">
        Unrenderable item ({item.kind}) — reload to update the app.
      </div>
    );
  }
  return renderItem(item, context);
}
